import { BrowserRouter, MemoryRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Capacitor } from "@capacitor/core";
import { IconContext } from "@phosphor-icons/react";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";

import { OnboardingPage } from "./pages/OnboardingPage";
import { PricingPage } from "./pages/PricingPage";
import { LoginPage } from "./pages/LoginPage";
import { SignupPage } from "./pages/SignupPage";
import { ContactPage } from "./pages/ContactPage";
import { SupportPage } from "./pages/SupportPage";
import { DashboardPage } from "./pages/DashboardPage";
import { NewProjectPage } from "./pages/NewProjectPage";
import { StudioPage } from "./pages/StudioPage";
import { ProjectDetailPage } from "./pages/ProjectDetailPage";
import { ReviewPage } from "./pages/ReviewPage";
import { OperatorDashboard } from "./pages/operator/OperatorDashboard";
import DesignSystemPage from "./pages/DesignSystemPage";

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 1000 * 60 * 5, retry: 2 } },
});

// Capacitor native WebView requires MemoryRouter (no real server for URL routing)
// Web uses BrowserRouter for proper URL support
const Router = Capacitor.isNativePlatform() ? MemoryRouter : BrowserRouter;

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <IconContext.Provider value={{ color: "currentColor", weight: "duotone" }}>
        <Router>
          <AuthProvider>
            <Routes>
              {/* Public */}
              <Route path="/" element={<OnboardingPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/support" element={<SupportPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/design-system" element={<ProtectedRoute requireRole="operator"><DesignSystemPage /></ProtectedRoute>} />

              {/* Protected — most specific first */}
              <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
              <Route path="/new-project" element={<ProtectedRoute><NewProjectPage /></ProtectedRoute>} />
              <Route path="/project/:projectId/studio" element={<ProtectedRoute><StudioPage /></ProtectedRoute>} />
              <Route path="/project/:projectId/upload" element={<Navigate to="../studio" replace />} />
              <Route path="/project/:projectId/review" element={<ProtectedRoute><ReviewPage /></ProtectedRoute>} />
              <Route path="/project/:projectId" element={<ProtectedRoute><ProjectDetailPage /></ProtectedRoute>} />

              {/* Operator — R05: gated behind operator custom claim, not just authentication */}
              <Route path="/operator" element={<ProtectedRoute requireRole="operator"><OperatorDashboard /></ProtectedRoute>} />

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AuthProvider>
        </Router>
      </IconContext.Provider>
    </QueryClientProvider>
  );
}
